import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import useSharePointList from '../hooks/useSharePointList';
import usePermissions from '../auth/usePermissions';
import usePlantScope from '../auth/usePlantScope';
import { createSupplier, listSuppliers } from '../services/suppliers';
import { createSupplierDocument } from '../services/supplierDocuments';
import { listParts } from '../services/parts';
import { listPlants } from '../services/plants';
import { formatCountry, formatDateShort } from '../utils/formatters';
import SupplierForm from './SupplierForm';

// Coverage-status pill colors — mirrors CoverageStatusBadge in
// SupplierDetail.jsx so the status pills read the same on the list and the
// detail page (per Jillian, Aug 2026, instead of every pill being gray):
// blue Prospecting → purple Proposal Out → green Active → teal On Call →
// amber Paused → orange Suspended → red Closed → slate Declined.
const COVERAGE_STATUS_COLORS = {
  Prospecting: { bg: 'rgba(30, 64, 175, 0.1)', fg: '#1e40af' },
  'Proposal Out': { bg: 'rgba(126, 34, 206, 0.1)', fg: '#7e22ce' },
  Active: { bg: 'rgba(22, 163, 74, 0.1)', fg: 'var(--brand-success, #16a34a)' },
  'On Call': { bg: 'rgba(13, 148, 136, 0.12)', fg: '#0d9488' },
  Paused: { bg: 'rgba(202, 138, 4, 0.1)', fg: '#ca8a04' },
  Suspended: { bg: 'rgba(234, 88, 12, 0.12)', fg: '#ea580c' },
  Closed: { bg: 'rgba(220, 38, 38, 0.1)', fg: 'var(--brand-danger, #dc2626)' },
  Declined: { bg: 'rgba(100, 116, 139, 0.12)', fg: '#475569' },
};

/**
 * Suppliers index. Managers can add new suppliers; all roles can click through
 * to a supplier's detail page. Contacts live on the detail page to keep this
 * table compact.
 */
export default function Suppliers() {
  // canAddSuppliers already accounts for mobile (add/edit is off on phones).
  // usePermissions spreads the auth fields, so name/email come through here
  // too — used to stamp PostedBy on an uploaded Blanket PO file.
  const { canAddSuppliers, isDirector, isLiaisonManager, name, email } =
    usePermissions();
  // Liaison Managers only see suppliers with coverage at their assigned
  // plants (per Jillian, July 2026) — same scoping the Plants pages use.
  // RMs and Directors keep the full list.
  const {
    allows: allowsPlant,
    allowsSupplier,
    loading: scopeLoading,
  } = usePlantScope();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useSharePointList(listSuppliers);

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Secondary data used to derive the Plant/s column. Two sources feed
  // it: parts' plantVehicles (every plant a supplier's parts ship to) and
  // each supplier's direct plantAssignments (on-site coverage entries
  // from the Plant Locations & Coverage table). Failures collapse the
  // column to an em-dash rather than breaking the table.
  const [parts, setParts] = useState([]);
  const [plants, setPlants] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listParts()
      .then((rows) => {
        if (!cancelled) setParts(rows || []);
      })
      .catch(() => {
        if (!cancelled) setParts([]);
      });
    listPlants()
      .then((rows) => {
        if (!cancelled) setPlants(rows || []);
      })
      .catch(() => {
        if (!cancelled) setPlants([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Set of codes already in use. Passed to the form so it can propose
  // the next free number and block duplicates before hitting SharePoint.
  const existingCodes = useMemo(() => {
    const set = new Set();
    (data || []).forEach((s) => {
      if (s?.SupplierCode) set.add(String(s.SupplierCode).toUpperCase().trim());
    });
    return set;
  }, [data]);

  // Group parts by SupplierCode so each supplier row can enumerate its
  // derived plant codes in O(1).
  const partsBySupplier = useMemo(() => {
    const map = {};
    (parts || []).forEach((p) => {
      const code = String(p.SupplierCode || '').toUpperCase().trim();
      if (!code) return;
      if (!map[code]) map[code] = [];
      map[code].push(p);
    });
    return map;
  }, [parts]);

  // Enrich each supplier row with the set of plant codes that belong to
  // it. Plants come from BOTH the supplier's parts (plantVehicles) and
  // the supplier's direct plantAssignments — so a supplier shows up on
  // its plants even when no parts have been linked yet. We keep both a
  // deduped + sorted array (for pill rendering) and a flattened string
  // (so the DataTable's generic search + sort still work).
  //
  // The Parts column is the same idea one level in: we keep the full
  // part objects (so the render can link each pill to its detail page)
  // plus a joined-string partsText for generic search + sort.
  const rows = useMemo(() => {
    return (data || []).map((s) => {
      const code = String(s.SupplierCode || '').toUpperCase().trim();
      const supplierParts = partsBySupplier[code] || [];
      const partsPlantCodes = supplierParts
        .flatMap((p) => (p.plantVehicles || []).map((pv) => pv.plantCode))
        .filter(Boolean);
      const directPlantCodes = (s.plantAssignments || [])
        .map((pa) => pa.plantCode)
        .filter(Boolean);
      const plantCodes = Array.from(
        new Set(
          [...partsPlantCodes, ...directPlantCodes].map((c) =>
            String(c).toUpperCase(),
          ),
        ),
      ).sort();
      // Sort parts alphabetically by Title so the pills render in a
      // stable order on every paint, and dedupe defensively in case the
      // Parts list ever has two rows with the same title for the same
      // supplier (shouldn't happen, but cheap to guard).
      const partsList = [...supplierParts]
        .filter((p) => p && p.Title)
        .sort((a, b) =>
          String(a.Title).localeCompare(String(b.Title), undefined, {
            sensitivity: 'base',
          }),
        );
      const partTitles = partsList.map((p) => p.Title);
      // Distinct coverage statuses across this supplier's plant
      // assignments (e.g. "Active", "Proposal Out") for the Status
      // column. A supplier can cover several plants at different
      // statuses, so we surface the unique set.
      const statuses = Array.from(
        new Set(
          (s.plantAssignments || [])
            .map((pa) => (pa.coverageStatus || '').trim())
            .filter(Boolean),
        ),
      );
      // Flatten each contact's identifying fields into one searchable
      // string so the DataTable's free-text search matches on contact
      // names / titles / locations / emails / phones. Stored under a
      // hidden column (no render, search-only) so the visible row
      // layout doesn't change. Phones go in twice — once formatted and
      // once digits-only — so a search like "5551234" still hits a
      // saved "(555) 123-4567".
      const contactBits = (s.contacts || []).flatMap((c) => {
        if (!c) return [];
        const phone = c.phone || '';
        const officePhone = c.officePhone || '';
        return [
          c.name,
          c.title,
          c.location,
          c.email,
          c.role,
          phone,
          phone.replace(/\D/g, ''),
          officePhone,
          officePhone.replace(/\D/g, ''),
        ].filter(Boolean);
      });
      const contactsText = contactBits.join(' ').toLowerCase();
      return {
        ...s,
        plantCodes,
        plantsText: plantCodes.join(', '),
        partsList,
        partTitles,
        partsText: partTitles.join(', '),
        statuses,
        statusText: statuses.join(', '),
        contactsText,
      };
    });
  }, [data, partsBySupplier]);

  const onNew = () => setCreating(true);

  const onSave = useCallback(
    async (model, poFiles) => {
      setSaving(true);
      try {
        await createSupplier(model);
        // Upload the attached PO files (one per plant PO, if any) to the
        // new supplier's Documents. Best-effort — the supplier already
        // saved, so a failed upload only warns rather than blocking.
        for (const { file, category, number, plantCode } of poFiles || []) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await createSupplierDocument({
              file,
              supplierCode: String(model.SupplierCode || '').trim(),
              Title: `${category} ${number || ''}${plantCode ? ` (${plantCode})` : ''}`.trim(),
              Category: category,
              PostedBy: name || email || '',
            });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[suppliers] PO file upload failed', err);
            window.alert(
              `The supplier was created, but the PO file upload for ${plantCode || 'a plant'} failed: ${
                err?.body?.error?.message || err?.message || 'Unknown error.'
              }`,
            );
          }
        }
        setCreating(false);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh, name, email],
  );

  const columns = [
    // Account # holds short 3–4 digit numbers, so it doesn't need much
    // width — narrowed + compact padding so the Supplier column moves left
    // and gets more room (per Jillian, Aug 2026).
    { key: 'SupplierCode', label: 'Account #', width: 90, compact: true },
    { key: 'Title', label: 'Supplier' },
    // City / State / Country are trimmed from the phone view — the mobile
    // search results show only Account #, Supplier, Plant/s, and Status; the
    // rest live on the supplier detail page.
    { key: 'City', label: 'City', mobileHidden: true },
    { key: 'State', label: 'State', mobileHidden: true },
    { key: 'Country', label: 'Country', mobileHidden: true, render: (r) => formatCountry(r.Country) },
    {
      key: 'plantsText',
      label: 'Plant/s',
      mobileHidden: true,
      render: (r) =>
        r.plantCodes?.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {r.plantCodes.map((pc) => (
              <span key={pc} className="badge plant">
                {pc}
              </span>
            ))}
          </div>
        ) : (
          <span className="muted">—</span>
        ),
    },
    // Status column. A supplier's coverage status(es) across its plant
    // assignments, shown as pills (a supplier may cover several plants at
    // different statuses, e.g. "Active" + "Proposal Out").
    {
      key: 'statusText',
      label: 'Status',
      mobileHidden: true,
      render: (r) =>
        r.statuses?.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {r.statuses.map((st) => {
              // Keep the shared `badge` class (shape + uppercase typography)
              // and just override the colors inline so each status reads in
              // its lifecycle color instead of gray. Unknown statuses fall
              // back to the neutral gray badge.
              const c = COVERAGE_STATUS_COLORS[st];
              return (
                <span
                  key={st}
                  className={c ? 'badge' : 'badge neutral'}
                  style={c ? { background: c.bg, color: c.fg } : undefined}
                >
                  {st}
                </span>
              );
            })}
          </div>
        ) : (
          <span className="muted">—</span>
        ),
    },
    // Last Updated — SharePoint's Modified system column. Sort key is
    // the parsed timestamp so newest/oldest ordering is chronological.
    // Falls back to Created for rows that haven't been edited since.
    {
      key: 'Modified',
      label: 'Last Updated',
      mobileHidden: true,
      sortValue: (r) => new Date(r.Modified || r.Created || 0).getTime(),
      render: (r) => {
        const v = r.Modified || r.Created;
        return v ? formatDateShort(v) : <span className="muted">—</span>;
      },
    },
    // Hidden contacts-search column. `hidden: true` keeps it out of
    // the rendered head/body but still indexed by the toolbar's free
    // text search, so typing a contact's name, title, email, or
    // phone digits finds the supplier they belong to.
    {
      key: 'contactsText',
      label: 'Contacts',
      hidden: true,
      sortable: false,
    },
  ];

  const onRowClick = (row) => {
    if (row.SupplierCode) {
      navigate(`/suppliers/${encodeURIComponent(row.SupplierCode)}`);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>Suppliers</h1>
        <div className="actions">
          {canAddSuppliers && (
            <button className="btn btn-primary" onClick={onNew}>
              + New Supplier
            </button>
          )}
        </div>
      </div>

      {(loading || (isLiaisonManager && scopeLoading)) && <Loading />}
      {error && (
        <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
          <div className="card-title">Couldn't Load Suppliers</div>
          <p>{error.message}</p>
          <button className="btn btn-outline btn-sm" onClick={refresh}>
            Retry
          </button>
        </div>
      )}
      {!loading && !error && !(isLiaisonManager && scopeLoading) && (
        <DataTable
          rows={
            isLiaisonManager
              ? rows.filter(
                  (r) =>
                    (r.plantCodes || []).some((pc) => allowsPlant(pc)) &&
                    // Sep 2026: LMs also only see the suppliers they're
                    // responsible for (see utils/supplierScope.js).
                    allowsSupplier(r.SupplierCode),
                )
              : rows
          }
          columns={columns}
          onRowClick={onRowClick}
          mobileSearchOnly
          searchLabel="Search Suppliers"
          emptyMessage={
            isLiaisonManager
              ? 'No suppliers at your assigned plants yet.'
              : 'No suppliers yet. Click “+ New Supplier” to add one.'
          }
        />
      )}

      <Modal
        open={creating}
        onClose={() => (saving ? null : setCreating(false))}
        title="New Supplier"
      >
        {creating && (
          <SupplierForm
            initial={{}}
            existingCodes={existingCodes}
            plants={plants}
            // Billing & PO fields are Director-only, even for creators
            // granted an add-supplier override (per Jillian, July 2026).
            canSeeBilling={isDirector}
            saving={saving}
            onSave={onSave}
            onCancel={() => setCreating(false)}
          />
        )}
      </Modal>
    </>
  );
}
